import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CalendarCheck, Check, Clock, Loader2, Lock, TriangleAlert } from 'lucide-react';

/**
 * A session the practitioner put forward, for the client to confirm.
 *
 * No sign-in: most of a practitioner's clients will never be members here, and the one-time
 * link in their email IS the credential (owner, Jun 2026). If there is something to pay, they
 * pay here, through the same Stripe checkout as any other booking — which is why this is not
 * an invoice: the money moves the moment they say yes.
 */
const CardFields = ({ amount, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    setBusy(false);
    if (error) {
      toast.error(error.message || 'Your card could not be charged.');
      return;
    }
    onPaid();
  };

  return (
    <form onSubmit={submit} data-testid="proposal-card-form">
      <PaymentElement />
      <Button type="submit" className="w-full mt-4" disabled={busy || !stripe}
        data-testid="proposal-pay-btn">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</>
              : <><Lock className="w-4 h-4 mr-2" /> Pay ${Number(amount).toFixed(2)}</>}
      </Button>
    </form>
  );
};

const niceWhen = (iso, tz) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone: tz || undefined, timeZoneName: 'short',
    });
  } catch {
    return d.toLocaleString();
  }
};

export default function BookingProposalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [secret, setSecret] = useState('');
  const [amount, setAmount] = useState(0);
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bookings/proposal/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.status === 'confirmed') setDone('confirmed');
        if (r.data.status === 'cancelled') setDone('cancelled');
      })
      .catch((e) => setError(e?.response?.data?.detail || 'This link is no longer valid.'));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bookings/proposal/${token}/accept`);
      if (r.data.requires_payment) {
        setSecret(r.data.client_secret);
        setAmount(r.data.charge_total);
      } else {
        setDone('confirmed');
        toast.success('Confirmed — it is in the diary');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm that');
    } finally {
      setBusy(false);
    }
  };

  const paid = async () => {
    try {
      await axios.post(`${API}/bookings/proposal/${token}/paid`);
    } catch { /* the webhook settles it either way */ }
    setDone('confirmed');
    toast.success('Paid and confirmed — see you then');
  };

  const decline = async () => {
    if (!window.confirm('Let your practitioner know that time does not work?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/bookings/proposal/${token}/decline`);
      setDone('declined');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send that');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6"
        data-testid="proposal-error">
        <div className="max-w-md w-full bg-white border rounded-2xl p-8 text-center">
          <TriangleAlert className="w-9 h-9 text-amber-500 mx-auto mb-4" />
          <h1 className="text-base md:text-lg font-semibold mb-2">This link won&apos;t open</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="proposal-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-5" data-testid="proposal-page">
      <div className="max-w-xl mx-auto bg-white border rounded-2xl overflow-hidden">
        <div className="px-7 pt-7 pb-5 border-b">
          <CalendarCheck className="w-7 h-7 text-emerald-700 mb-3" />
          <h1 className="text-lg md:text-xl font-semibold" data-testid="proposal-title">
            {data.session_title}
          </h1>
          <p className="text-sm text-gray-600 mt-1" data-testid="proposal-when">
            {niceWhen(data.scheduled_at, data.timezone)}
          </p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {data.duration_minutes} minutes · with{' '}
            {data.practitioner_name}
          </p>
        </div>

        <div className="px-7 py-6 space-y-5">
          {data.note && (
            <p className="text-sm text-gray-700 italic border-l-2 border-gray-200 pl-3"
              data-testid="proposal-note">
              {data.note}
            </p>
          )}

          {done === 'confirmed' && (
            <div className="text-center py-6" data-testid="proposal-confirmed">
              <Check className="w-9 h-9 text-emerald-600 mx-auto mb-3" />
              <p className="text-sm font-medium">You&apos;re booked in</p>
              <p className="text-xs text-gray-500 mt-1">
                Your practitioner has been told. Anything you need to fill in beforehand will
                arrive by email.
              </p>
            </div>
          )}

          {done === 'declined' && (
            <div className="text-center py-6" data-testid="proposal-declined">
              <p className="text-sm font-medium">Thanks for letting us know</p>
              <p className="text-xs text-gray-500 mt-1">
                Your practitioner has been told that time doesn&apos;t work and will be in touch.
              </p>
            </div>
          )}

          {done === 'cancelled' && (
            <p className="text-sm text-gray-600" data-testid="proposal-cancelled">
              This session was cancelled. Speak to your practitioner if you&apos;d like another time.
            </p>
          )}

          {!done && secret && (
            <div className="space-y-3" data-testid="proposal-payment">
              <p className="text-sm text-gray-700">
                One more step — ${Number(amount).toFixed(2)} to hold your place.
              </p>
              <Elements stripe={stripePromise}
                options={{ clientSecret: secret, appearance: { theme: 'stripe' } }}>
                <CardFields amount={amount} onPaid={paid} />
              </Elements>
            </div>
          )}

          {!done && !secret && (
            <>
              {data.needs_payment && data.settle === 'client' && (
                <p className="text-sm text-gray-700" data-testid="proposal-price">
                  ${Number(data.charge_total).toFixed(2)} to pay when you confirm.
                </p>
              )}
              {data.settle === 'credit' && (
                <p className="text-sm text-gray-700" data-testid="proposal-credit">
                  This session uses one you have already paid for.
                </p>
              )}
              {data.settle === 'outside' && (
                <p className="text-sm text-gray-700" data-testid="proposal-outside">
                  Payment for this one is arranged directly with your practitioner.
                </p>
              )}
              <div className="flex flex-wrap gap-3">
                <Button onClick={accept} disabled={busy} data-testid="proposal-accept-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Yes, that time works
                </Button>
                <Button variant="outline" onClick={decline} disabled={busy}
                  data-testid="proposal-decline-btn">
                  That time doesn&apos;t work
                </Button>
              </div>
              {data.expires_at && (
                <p className="text-xs text-gray-400" data-testid="proposal-expiry">
                  Please answer by {new Date(data.expires_at).toLocaleDateString()} — after that
                  the slot goes back to your practitioner&apos;s diary.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
